// @flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Columns,
    Column,
    Flex,
    Stack,
    TextField
} from '@locus-taxy/neo-ui';
import { isEnumControl, or, Paths, rankWith, uiTypeIs } from '@jsonforms/core';

import useArrayControlProps from '../util/useArrayControlProps';

const getDefaultValue = (
    key: string,
    others: { [key: string]: string } = {}
) => [{ ...others, [key]: '' }];
type Props = {};

// TODO: There is another control ArrayTextInputControl which can be combined
// to this. Requires few modifications to displaying child error.
const ArrayObjectInputControl = (props: Props): React.Node => {
    const { t } = useTranslation('platform');

    const {
        description,
        data,
        enabled,
        handleChange,
        label,
        path,
        required,
        removeItems,
        schema,
        translationKey
    } = useArrayControlProps(props);

    const { key, others } = schema;
    // To show a row when data is undefined or [] we add empty value and show a default row.
    const items = data?.length ? data : [getDefaultValue(key, others)];
    const actionLabel = t(`${translationKey}.actionLabel`);

    React.useEffect(() => {
        // Edge case:
        // Whenever we have only one item and that is [''] then remove that from data.
        // This helps avoids the case where we have unnecessary change detection in the following scenario.
        //
        // initial data - []
        // we add some text - ['some text']
        // we delete the text from text input - ['']
        // Now as per users point of view there is no change because the text updated is now deleted. But JsonForm
        // has an item ['']. So to cover this case deleting that item.
        if (data.length === 1 && !data[0]) {
            handleChange(path, []);
        }
    }, [data, handleChange, path]);

    const getChildPath = index => {
        return Paths.compose(path, `${index}`);
    };

    const handleAdd = () => {
        handleChange(path, items.concat(getDefaultValue(key, others)));
    };

    return (
        <Stack space="medium">
            {items.map((item, index) => {
                const childPath = getChildPath(index);
                // TODO: Support validation from the schema
                const error =
                    required && !item
                        ? t('settings.common.requiredFieldMessage')
                        : undefined;

                return (
                    <Columns key={childPath}>
                        <Column width="fluid">
                            <TextField
                                label={`${label} ${index + 1}`}
                                value={item[key]}
                                fullWidth
                                required={required}
                                error={!!error}
                                disabled={!enabled}
                                helperText={error ?? description}
                                onChange={value =>
                                    handleChange(childPath, {
                                        ...item,
                                        [key]: value
                                    })
                                }
                            />
                        </Column>
                        {data?.length > 1 && (
                            <Column width="content">
                                <Box mt="2" ml="2">
                                    <Button
                                        disabled={!enabled}
                                        appearance="icon"
                                        color="grey.600"
                                        size="medium"
                                        icon="delete"
                                        onClick={removeItems(path, [index])}
                                    />
                                </Box>
                            </Column>
                        )}
                    </Columns>
                );
            })}

            <Flex alignSelf="flex-end">
                <Button onClick={handleAdd} palette="primary">
                    {actionLabel}
                </Button>
            </Flex>
        </Stack>
    );
};

export default ArrayObjectInputControl;

export const arrayObjectInputControlTester: any = rankWith(
    1,
    or(uiTypeIs('ArrayObjectInputControl'), isEnumControl)
);
